import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@tellescope/ui";
import { cn } from "@tellescope/utils";
import { TIMEZONES } from "@tellescope/types-models";
import { TellescopeFormFieldBaseProps } from "../types";

export interface TimezoneFieldProps extends TellescopeFormFieldBaseProps<"Timezone"> {
  placeholder?: string;
  wrapperClassName?: string;
}

export const TimezoneField = ({
  title,
  value,
  onChange,
  className,
  error,
  onBlur,
  touched,
  placeholder,
  wrapperClassName,
}: TimezoneFieldProps) => {
  const { t } = useTranslation("forms");
  const showError = !!(error && touched);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Filter timezones based on search query
  const filteredTimezones = useMemo(() => {
    if (!searchQuery) return TIMEZONES;
    const lowerSearch = searchQuery.toLowerCase();
    return TIMEZONES.filter((timezone) => {
      const translatedTimezone = timezone.toLowerCase();
      return translatedTimezone.includes(lowerSearch) || timezone.toLowerCase().includes(lowerSearch);
    });
  }, [searchQuery]);

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setOpen(false);
  };

  return (
    <div className={cn("space-y-1", wrapperClassName)}>
      {!!title && <h4>{title}</h4>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between font-normal h-10",
              !value && "text-muted-foreground",
              className,
              showError && "border-destructive"
            )}
            onBlur={onBlur}
          >
            <span className="truncate">{value || placeholder || t("Select a timezone")}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <Command shouldFilter={false}>
            <CommandInput
              value={searchQuery}
              onValueChange={setSearchQuery}
              placeholder={t("Search timezones...")}
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>{t("No timezone found")}</CommandEmpty>
              <CommandGroup>
                {filteredTimezones.map((timezone) => (
                  <CommandItem
                    key={timezone}
                    value={timezone}
                    onSelect={() => (value === timezone ? handleSelect("") : handleSelect(timezone))}
                  >
                    {timezone}
                    <Check className={cn("ml-auto h-4 w-4", value === timezone ? "opacity-100" : "opacity-0")} />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {showError && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
